import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Flame, Cloud, Lock, Loader2, Printer, FileSpreadsheet, Trash2, Pencil, Plus, X, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const TODAY_ISO = () => new Date().toISOString().slice(0, 10);
const THIS_YEAR = () => new Date().getFullYear();
const THIS_MONTH = () => new Date().getMonth() + 1;

// Given a target month/year, produce an ISO date string on the 1st (or today
// if the target is the current month) — used by "Quick-add this month's
// missing drill" buttons.
function firstOfMonthISO(year, month) {
  if (year === THIS_YEAR() && month === THIS_MONTH()) return TODAY_ISO();
  const mm = String(month).padStart(2, "0");
  return `${year}-${mm}-01`;
}

export default function DrillLog() {
  const [year, setYear] = useState(THIS_YEAR());
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [center, setCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {}/{...} → open dialog
  const [busy, setBusy] = useState(false);
  // iter-81e — user-selectable theme for exported PDFs. Persisted per-browser.
  const [theme, setTheme] = useState(() => localStorage.getItem("cc_drill_theme") || "emerald");
  const setThemePersist = (t) => { setTheme(t); try { localStorage.setItem("cc_drill_theme", t); } catch {/* ignore */} };

  const load = async () => {
    setLoading(true);
    try {
      const [c, r, s] = await Promise.all([
        api.get("/center/me").then((r) => r.data),
        api.get(`/drill-logs?year=${year}`).then((r) => r.data),
        api.get(`/drill-logs/compliance-summary?year=${year}`).then((r) => r.data),
      ]);
      setCenter(c);
      setRows(r);
      setSummary(s);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const remove = async (row) => {
    if (!window.confirm(`Delete the ${row.drill_type} drill from ${row.date}?`)) return;
    try { await api.delete(`/drill-logs/${row.id}`); toast.success("Deleted."); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadCSV = async () => {
    try {
      const r = await api.get(`/drill-logs.csv?year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `drill-log-${year}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const printPDF = async () => {
    try {
      const r = await api.get(`/drill-logs.pdf?year=${year}&theme=${theme}&t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const frame = document.createElement("iframe");
      frame.style.position = "fixed"; frame.style.right = "0"; frame.style.bottom = "0";
      frame.style.width = "0"; frame.style.height = "0"; frame.style.border = "0";
      frame.src = url;
      frame.onload = () => {
        try { frame.contentWindow?.focus(); frame.contentWindow?.print(); } catch { /* ignored */ }
        setTimeout(() => { URL.revokeObjectURL(url); frame.remove(); }, 60_000);
      };
      document.body.appendChild(frame);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadPDF = async () => {
    try {
      const r = await api.get(`/drill-logs.pdf?year=${year}&theme=${theme}&t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `drill-log-${year}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadAuditCover = async () => {
    try {
      const r = await api.get(`/audit/cover-sheet.pdf?year=${year}&theme=${theme}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `audit-cover-${year}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const yearOptions = useMemo(() => {
    const t = THIS_YEAR();
    return [t + 1, t, t - 1, t - 2, t - 3];
  }, []);

  return (
    <section data-testid="drill-log-page" className="max-w-6xl mx-auto p-6 space-y-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-emerald-700">
            <ShieldCheck className="w-4 h-4" /> OAC 5101:2-12-34
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-medium text-stone-900 tracking-tight">
            Fire &amp; Tornado Drill Log
          </h1>
          <p className="mt-2 text-sm text-stone-600 max-w-2xl leading-relaxed">
            Fire drills — monthly, year-round (12/yr). Tornado / severe-weather drills — monthly March through September (7/yr). Retain records for a minimum of three years or until an audit is completed.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Year</Label>
            <select
              data-testid="drill-log-year"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="mt-1 h-11 rounded-xl border border-stone-200 px-3 text-sm bg-white"
            >
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-stone-500">PDF Theme</Label>
            <div data-testid="drill-theme-picker" className="mt-1 inline-flex h-11 rounded-xl border border-stone-200 bg-white overflow-hidden">
              {[
                { k: "emerald",  label: "Emerald",  dot: "#065f46" },
                { k: "navy",     label: "Navy",     dot: "#0f172a" },
                { k: "charcoal", label: "Charcoal", dot: "#111827" },
              ].map((t) => (
                <button
                  key={t.k}
                  data-testid={`drill-theme-${t.k}`}
                  onClick={() => setThemePersist(t.k)}
                  className={`flex items-center gap-1.5 px-3 text-sm font-medium border-r border-stone-200 last:border-r-0 transition-colors ${theme === t.k ? "bg-stone-900 text-white" : "text-stone-700 hover:bg-stone-50"}`}
                  title={`${t.label} theme`}
                >
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: t.dot }} />
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <Button data-testid="drill-log-print" onClick={printPDF} variant="outline" className="rounded-xl h-11">
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
          <Button data-testid="drill-log-download-pdf" onClick={downloadPDF} variant="outline" className="rounded-xl h-11">
            <Printer className="w-4 h-4 mr-1.5" /> Download PDF
          </Button>
          <Button data-testid="drill-log-audit-cover" onClick={downloadAuditCover} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
            <ShieldCheck className="w-4 h-4 mr-1.5" /> Audit Cover Sheet
          </Button>
          <Button data-testid="drill-log-download-csv" onClick={downloadCSV} variant="outline" className="rounded-xl h-11">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> CSV
          </Button>
          <Button data-testid="drill-log-add" onClick={() => setEditing({})} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
            <Plus className="w-4 h-4 mr-1.5" /> Log a drill
          </Button>
        </div>
      </header>

      {/* Center-name / license / year strip (mirrors the PDF header) */}
      <div data-testid="drill-log-header-strip" className="rounded-2xl border border-stone-200 bg-white p-4 flex flex-wrap gap-6 items-center">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Center</div>
          <div className="font-display text-lg text-stone-900">{center?.name || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Ohio License #</div>
          <div className="font-display text-lg text-stone-900" data-testid="drill-log-license-number">
            {center?.license_number || <span className="text-amber-700 text-sm">not set — add it on Center Settings</span>}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Year</div>
          <div className="font-display text-lg text-stone-900">{year}</div>
        </div>
      </div>

      {/* Compliance progress */}
      {summary && (
        <div className="grid md:grid-cols-3 gap-4">
          <CompliancePill kind="fire" summary={summary.fire} year={year} onQuickAdd={(m) => setEditing({ drill_type: "fire", date: firstOfMonthISO(year, m) })} />
          <CompliancePill kind="tornado" summary={summary.tornado} year={year} onQuickAdd={(m) => setEditing({ drill_type: "tornado", date: firstOfMonthISO(year, m) })} />
          <CompliancePill kind="lockdown" summary={summary.lockdown} year={year} onQuickAdd={() => setEditing({ drill_type: "lockdown", date: TODAY_ISO() })} />
        </div>
      )}

      {/* Log table */}
      {loading ? (
        <div className="py-16 text-center text-stone-500 text-sm">
          <Loader2 className="w-4 h-4 inline-block mr-2 animate-spin" /> Loading drill log…
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="drill-log-empty" className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 p-10 text-center">
          <p className="text-sm text-stone-500">No drills logged for {year} yet.</p>
          <Button onClick={() => setEditing({})} className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">
            <Plus className="w-4 h-4 mr-1.5" /> Log the first drill
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid="drill-log-table">
            <thead className="bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest text-stone-500">
              <tr>
                <th className="text-left px-3 py-2.5">Date</th>
                <th className="text-left px-3 py-2.5">Type</th>
                <th className="text-left px-3 py-2.5">Start</th>
                <th className="text-left px-3 py-2.5">Finished</th>
                <th className="text-right px-3 py-2.5">Children</th>
                <th className="text-right px-3 py-2.5">Adults</th>
                <th className="text-left px-3 py-2.5">Safe?</th>
                <th className="text-left px-3 py-2.5">Signature</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`drill-log-row-${r.id}`} className="border-t border-stone-100 hover:bg-stone-50">
                  <td className="px-3 py-2.5 whitespace-nowrap">{r.date}</td>
                  <td className="px-3 py-2.5"><DrillTypePill type={r.drill_type} /></td>
                  <td className="px-3 py-2.5 text-stone-600">{r.time || "—"}</td>
                  <td className="px-3 py-2.5 text-stone-600">{r.finish_time || "—"}</td>
                  <td className="px-3 py-2.5 text-right">{r.num_children ?? "—"}</td>
                  <td className="px-3 py-2.5 text-right">{r.num_staff ?? "—"}</td>
                  <td className="px-3 py-2.5">{r.safe_location_reached === true ? "Yes" : r.safe_location_reached === false ? "No" : "—"}</td>
                  <td className="px-3 py-2.5 text-stone-700">{r.completed_by || "—"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-right">
                    <Button data-testid={`drill-log-edit-${r.id}`} variant="ghost" size="sm" onClick={() => setEditing(r)} className="h-8">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid={`drill-log-delete-${r.id}`} variant="ghost" size="sm" onClick={() => remove(r)} className="h-8 text-rose-700 hover:text-rose-900">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DrillDialog
        open={!!editing}
        initial={editing}
        busy={busy}
        onClose={() => setEditing(null)}
        onSave={async (payload) => {
          setBusy(true);
          try {
            if (editing?.id) await api.put(`/drill-logs/${editing.id}`, payload);
            else await api.post("/drill-logs", payload);
            toast.success("Drill logged.");
            setEditing(null);
            await load();
          } catch (err) { toast.error(formatApiError(err)); }
          finally { setBusy(false); }
        }}
      />

      {/* iter-81p — Carbon Monoxide Test Tracker sits directly under the
          Drill Log so directors keep both sheets in the same binder tab. */}
      <CoTestTracker year={year} theme={theme} />
    </section>
  );
}


function DrillTypePill({ type }) {
  if (type === "fire")     return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-800 border border-red-200"><Flame className="w-3 h-3" /> Fire</span>;
  if (type === "tornado")  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-sky-50 text-sky-800 border border-sky-200"><Cloud className="w-3 h-3" /> Tornado</span>;
  if (type === "lockdown") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200"><Lock className="w-3 h-3" /> Lock Down</span>;
  return <span className="text-xs text-stone-500">—</span>;
}


function CompliancePill({ kind, summary, year, onQuickAdd }) {
  const isFire = kind === "fire";
  const isLock = kind === "lockdown";
  const Icon = isFire ? Flame : (isLock ? Lock : Cloud);
  const label = isFire ? "Fire drills" : isLock ? "Lock Down drills" : "Tornado / Severe-Weather";
  const cadence = isFire ? "monthly, year-round" : isLock ? "auto-mirrors Fire drills — no double entry" : "monthly, March – September";
  const done = summary.count;
  const req = summary.required;
  const pct = req ? Math.min(100, Math.round((done / req) * 100)) : (done > 0 ? 100 : 0);
  const isDone = req > 0 && done >= req;
  const missing = summary.missing_months || [];
  const bgIcon = isFire ? "bg-red-50 text-red-700" : isLock ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700";
  const barColor = isDone ? "bg-emerald-600" : isFire ? "bg-red-500" : isLock ? "bg-amber-500" : "bg-sky-500";
  return (
    <div data-testid={`drill-log-summary-${kind}`} className={`rounded-2xl border ${isDone ? "border-emerald-200 bg-emerald-50/40" : "border-stone-200 bg-white"} p-5`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className={`w-9 h-9 rounded-xl grid place-items-center ${bgIcon}`}>
            <Icon className="w-[18px] h-[18px]" />
          </div>
          <div>
            <div className="font-display text-lg text-stone-900">{label}</div>
            <div className="text-[11px] text-stone-500">{cadence}</div>
          </div>
        </div>
        <div className="text-right">
          <div className={`font-display text-2xl ${isDone ? "text-emerald-800" : "text-stone-900"}`}>{done}{req > 0 && <span className="text-stone-400 text-base">/{req}</span>}</div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500">{req > 0 ? "months logged" : "logged this year"}</div>
        </div>
      </div>
      {req > 0 && (
        <div className="mt-3 h-2 w-full rounded-full bg-stone-200 overflow-hidden">
          <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {missing.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5">Still needed for {year}</div>
          <div className="flex flex-wrap gap-1.5">
            {missing.map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`drill-log-quickadd-${kind}-${m}`}
                onClick={() => onQuickAdd(m)}
                className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-2 py-0.5 text-[11px] font-medium text-stone-700 hover:bg-emerald-700 hover:text-white hover:border-emerald-700 transition-colors"
                title={`Quick-add ${MONTH_NAMES[m-1]} drill`}
              >
                <Plus className="w-2.5 h-2.5" /> {MONTH_NAMES[m-1]}
              </button>
            ))}
          </div>
        </div>
      )}
      {isLock && req === 0 && (
        <div className="mt-3">
          <button
            type="button"
            data-testid="drill-log-add-lockdown"
            onClick={() => onQuickAdd(0)}
            className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-2 py-0.5 text-[11px] font-medium text-stone-700 hover:bg-emerald-700 hover:text-white hover:border-emerald-700 transition-colors"
          >
            <Plus className="w-2.5 h-2.5" /> Log a lock down drill
          </button>
        </div>
      )}
      {isDone && missing.length === 0 && (
        <div className="mt-3 text-[11px] text-emerald-800">All required months logged ✓</div>
      )}
    </div>
  );
}


function DrillDialog({ open, initial, busy, onClose, onSave }) {
  const [f, setF] = useState({});
  useEffect(() => {
    if (!open) return;
    setF({
      date: initial?.date || TODAY_ISO(),
      time: initial?.time || "",
      finish_time: initial?.finish_time || "",
      drill_type: initial?.drill_type || "fire",
      num_children: initial?.num_children ?? "",
      num_staff: initial?.num_staff ?? "",
      safe_location_reached: initial?.safe_location_reached ?? true,
      problems_observed: initial?.problems_observed || "",
      corrective_action: initial?.corrective_action || "",
      completed_by: initial?.completed_by || "",
      notes: initial?.notes || "",
    });
  }, [open, initial]);

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...f,
      num_children: f.num_children === "" ? null : parseInt(f.num_children, 10),
      num_staff: f.num_staff === "" ? null : parseInt(f.num_staff, 10),
    };
    onSave(payload);
  };

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const typeBtnBase = "flex-1 h-10 rounded-xl border text-sm font-medium inline-flex items-center justify-center gap-1.5";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="drill-log-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit drill entry" : "Log a drill"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Drill type</Label>
            <div className="mt-1.5 flex gap-2">
              <button type="button" data-testid="drill-log-type-fire" onClick={() => set("drill_type", "fire")}
                className={`${typeBtnBase} ${f.drill_type === "fire" ? "bg-red-600 border-red-600 text-white" : "border-stone-200 text-stone-700 hover:bg-stone-50"}`}>
                <Flame className="w-4 h-4" /> Fire
              </button>
              <button type="button" data-testid="drill-log-type-tornado" onClick={() => set("drill_type", "tornado")}
                className={`${typeBtnBase} ${f.drill_type === "tornado" ? "bg-sky-600 border-sky-600 text-white" : "border-stone-200 text-stone-700 hover:bg-stone-50"}`}>
                <Cloud className="w-4 h-4" /> Tornado
              </button>
              <button type="button" data-testid="drill-log-type-lockdown" onClick={() => set("drill_type", "lockdown")}
                className={`${typeBtnBase} ${f.drill_type === "lockdown" ? "bg-amber-600 border-amber-600 text-white" : "border-stone-200 text-stone-700 hover:bg-stone-50"}`}>
                <Lock className="w-4 h-4" /> Lock Down
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Date"><Input type="date" value={f.date || ""} onChange={(e) => set("date", e.target.value)} required data-testid="drill-log-input-date" /></Field>
            <Field label="Start time"><Input type="text" value={f.time || ""} placeholder="10:30 AM" onChange={(e) => set("time", e.target.value)} data-testid="drill-log-input-time" /></Field>
            <Field label="Finished time"><Input type="text" value={f.finish_time || ""} placeholder="10:38 AM" onChange={(e) => set("finish_time", e.target.value)} data-testid="drill-log-input-finish" /></Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="No. of children"><Input type="number" min={0} value={f.num_children ?? ""} onChange={(e) => set("num_children", e.target.value)} data-testid="drill-log-input-children" /></Field>
            <Field label="No. of adults"><Input type="number" min={0} value={f.num_staff ?? ""} onChange={(e) => set("num_staff", e.target.value)} data-testid="drill-log-input-staff" /></Field>
            <Field label="Safe location reached?">
              <div className="flex gap-2">
                <button type="button" data-testid="drill-log-safe-yes" onClick={() => set("safe_location_reached", true)}
                  className={`flex-1 h-10 rounded-xl border text-sm font-medium ${f.safe_location_reached === true ? "bg-emerald-700 border-emerald-700 text-white" : "border-stone-200 text-stone-700 hover:bg-stone-50"}`}>Yes</button>
                <button type="button" data-testid="drill-log-safe-no" onClick={() => set("safe_location_reached", false)}
                  className={`flex-1 h-10 rounded-xl border text-sm font-medium ${f.safe_location_reached === false ? "bg-rose-700 border-rose-700 text-white" : "border-stone-200 text-stone-700 hover:bg-stone-50"}`}>No</button>
              </div>
            </Field>
          </div>

          <Field label="Signature (staff person completing log)"><Input value={f.completed_by || ""} placeholder="Jane Doe" onChange={(e) => set("completed_by", e.target.value)} data-testid="drill-log-input-completedby" /></Field>
          <Field label="Problems observed (optional)"><Textarea value={f.problems_observed || ""} onChange={(e) => set("problems_observed", e.target.value)} rows={2} data-testid="drill-log-input-problems" /></Field>
          <Field label="Corrective action taken (optional)"><Textarea value={f.corrective_action || ""} onChange={(e) => set("corrective_action", e.target.value)} rows={2} data-testid="drill-log-input-action" /></Field>
          <Field label="Notes (optional)"><Textarea value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="drill-log-input-notes" /></Field>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button type="submit" disabled={busy} data-testid="drill-log-save" className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              {initial?.id ? "Update" : "Save entry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-stone-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}



// ============ iter-81p — Carbon Monoxide Test Tracker (12-month table) ==========
const CO_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function CoTestTracker({ year, theme }) {
  const [byMonth, setByMonth] = useState({}); // { 1: {...}, 2: {...} }
  const [dirtyMonths, setDirtyMonths] = useState(new Set());
  const [savingMonth, setSavingMonth] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/co-tests?year=${year}`);
      const m = {};
      for (const row of r.data || []) m[row.month] = row;
      setByMonth(m);
      setDirtyMonths(new Set());
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const setField = (month, field, val) => {
    setByMonth((prev) => ({
      ...prev,
      [month]: { ...(prev[month] || {}), month, year, [field]: val },
    }));
    setDirtyMonths((prev) => new Set(prev).add(month));
  };

  const saveMonth = async (month) => {
    const row = byMonth[month] || { month, year };
    setSavingMonth(month);
    try {
      await api.put("/co-tests", { ...row, year, month });
      setDirtyMonths((prev) => {
        const next = new Set(prev); next.delete(month); return next;
      });
      toast.success(`${CO_MONTHS[month - 1]} saved.`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingMonth(null); }
  };

  // iter-81ad — Print/Download must reflect what the user just typed even if
  // they never clicked Save. Auto-save every dirty month first, then fetch
  // the PDF. Fixes the complaint "PDF prints stale data".
  const flushDirty = async () => {
    if (dirtyMonths.size === 0) return;
    const months = Array.from(dirtyMonths);
    try {
      await Promise.all(months.map((m) => {
        const row = byMonth[m] || { month: m, year };
        return api.put("/co-tests", { ...row, year, month: m });
      }));
      setDirtyMonths(new Set());
    } catch (err) {
      toast.error(formatApiError(err));
      throw err;
    }
  };

  const printPDF = async () => {
    try {
      await flushDirty();
      const r = await api.get(`/co-tests.pdf?year=${year}&theme=${theme}&t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const frame = document.createElement("iframe");
      frame.style.position = "fixed"; frame.style.right = "0"; frame.style.bottom = "0";
      frame.style.width = "0"; frame.style.height = "0"; frame.style.border = "0";
      frame.src = url;
      frame.onload = () => {
        try { frame.contentWindow?.focus(); frame.contentWindow?.print(); } catch { /* ignored */ }
        setTimeout(() => { URL.revokeObjectURL(url); frame.remove(); }, 60_000);
      };
      document.body.appendChild(frame);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadPDF = async () => {
    try {
      await flushDirty();
      const r = await api.get(`/co-tests.pdf?year=${year}&theme=${theme}&t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `carbon-monoxide-tracker-${year}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <section data-testid="co-tracker-panel" className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-emerald-700">Also in this binder</div>
          <h2 className="font-display text-2xl text-stone-900">Carbon Monoxide Test Tracker · {year}</h2>
          <p className="text-xs text-stone-500 max-w-2xl mt-1">
            OAC 5101:2-12-13 requires a working CO detector on every floor where children are cared for. Record one test per month here.
          </p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="co-tracker-print" onClick={printPDF} variant="outline" className="rounded-xl h-10">
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
          <Button data-testid="co-tracker-download" onClick={downloadPDF} className="rounded-xl h-10 bg-emerald-700 hover:bg-emerald-800 text-white">
            <Printer className="w-4 h-4 mr-1.5" /> Download PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-stone-500 text-sm">
          <Loader2 className="w-4 h-4 inline-block mr-2 animate-spin" /> Loading CO tracker…
        </div>
      ) : (
        <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid="co-tracker-table">
            <thead className="bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest text-stone-500">
              <tr>
                <th className="text-left px-3 py-2.5 w-[110px]">Month</th>
                <th className="text-left px-3 py-2.5">Location tested</th>
                <th className="text-left px-3 py-2.5 w-[140px]">Date</th>
                <th className="text-left px-3 py-2.5 w-[100px]">Reading</th>
                <th className="text-left px-3 py-2.5 w-[160px]">Tested by</th>
                <th className="text-left px-3 py-2.5">Comment / remark</th>
                <th className="px-3 py-2.5 w-[80px]"></th>
              </tr>
            </thead>
            <tbody>
              {CO_MONTHS.map((label, i) => {
                const m = i + 1;
                const r = byMonth[m] || {};
                const isDirty = dirtyMonths.has(m);
                return (
                  <tr key={m} data-testid={`co-tracker-row-${m}`} className="border-t border-stone-100">
                    <td className="px-3 py-2 font-semibold text-emerald-800">{label}</td>
                    <td className="px-3 py-2">
                      <Input data-testid={`co-loc-${m}`} value={r.location || ""} onChange={(e) => setField(m, "location", e.target.value)}
                             className="h-9 rounded-lg" placeholder="Kitchen · Furnace room…" />
                    </td>
                    <td className="px-3 py-2">
                      <Input data-testid={`co-date-${m}`} type="date" value={r.date_tested || ""} onChange={(e) => setField(m, "date_tested", e.target.value)}
                             className="h-9 rounded-lg" />
                    </td>
                    <td className="px-3 py-2">
                      <Input data-testid={`co-reading-${m}`} value={r.reading || ""} onChange={(e) => setField(m, "reading", e.target.value)}
                             className="h-9 rounded-lg" placeholder="0 ppm" />
                    </td>
                    <td className="px-3 py-2">
                      <Input data-testid={`co-testedby-${m}`} value={r.tested_by || ""} onChange={(e) => setField(m, "tested_by", e.target.value)}
                             className="h-9 rounded-lg" placeholder="Name / initials" />
                    </td>
                    <td className="px-3 py-2">
                      <Input data-testid={`co-comment-${m}`} value={r.comments || ""} onChange={(e) => setField(m, "comments", e.target.value)}
                             className="h-9 rounded-lg" placeholder="OK / chirp / battery replaced…" />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Button
                        data-testid={`co-save-${m}`}
                        size="sm"
                        disabled={!isDirty || savingMonth === m}
                        onClick={() => saveMonth(m)}
                        className={`rounded-lg h-9 ${isDirty ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-stone-200 text-stone-400"}`}
                      >
                        {savingMonth === m ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isDirty ? "Save" : "Saved"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
